
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import { InvoiceManagementDto } from "@/dtos/invoiceManagementDto";
import { CreditOverdrawnOrderDto } from "@/dtos/creditOverdrawnOrderDto";
import rules from "@/store/rules";
import { MatchedInvoiceDto } from "@/dtos/matchedInvoiceDto";
import CreateMahnungDialog from "./CreateMahnungDialog.vue";
import CreditOverdrawnOrdersDialog from "./CreditOverdrawnOrdersDialog.vue";

@Component({
	components: {
		CreateMahnungDialog,
		CreditOverdrawnOrdersDialog,
	},
})
export default class OpenInvoices extends Vue {
	@Prop()
	private reloadInvoices!: boolean;
	private isLoading: boolean = false;
	private showGemahnt: boolean = true;
	private currentDueFilter: string = "";
	private positiveNumberRule = rules.requiredNonNegativeNumberRule;
	private mahnungNotDueInvoiceDialog: boolean = false;
	private currentInvoiceId: number = 0;
	private currentInvoiceDueDate: Date = new Date();
	private markAsPaidInvoiceAmount: number = 0.0;
	private markAsPaidInvoiceId: number = 0;
	private markAsPaidDialogOpen: boolean = false;
	private markAsPaidValid: boolean = false;
	private paidInvoiceIds: number[] = [];
	private validSearchTerm: string = "";
	private dueFilterTypes: any = [
		{
			value: "all",
			text: "Alle anzeigen",
		},
		{
			value: "due",
			text: "Nur fällige anzeigen",
		},
		{
			value: "duePlus10Days",
			text: "Nur 10+ Tage fällige anzeigen",
		},
	];

	private headers: Array<{
		text: string;
		value: string;
		align: string;
		sortable: boolean;
		width: string;
	}> = Array(
		{
			text: "Gemahnt",
			value: "hasMahnung",
			align: "left",
			sortable: true,
			width: "10px",
		},
		{
			text: "Rechnungs-Id",
			value: "invoiceId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Kunden-Id",
			value: "customerId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Auftrag-Id",
			value: "orderId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Fällig",
			value: "dueDate",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Rechnungskontakt",
			value: "invoiceAddress.companyName",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Region",
			value: "region",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Betrag",
			value: "totalAmount",
			align: "right",
			sortable: true,
			width: "1px",
		},
		{
			text: "",
			value: "menu",
			align: "right",
			sortable: false,
			width: "0px",
		}
	);

	private mounted() {
		this.currentDueFilter = this.dueFilterTypes[0].value;
		if (this.invoices.length === 0) {
			this.loadInvoices();
		}
	}

	@Watch("reloadInvoices")
	private async reload() {
		if (this.reloadInvoices && !this.isLoading) {
			await this.loadInvoices();
		}
		this.$emit("resetReloadToggle");
	}

	private get invoices() {
		let invoices = this.$store.state.dueInvoices;
		switch (this.currentDueFilter) {
			case "all":
			case "default":
				// unfiltered list
				break;
			case "due":
				invoices = invoices.filter((i: InvoiceManagementDto) => this.isDue(i.dueDate));
				break;
			case "duePlus10Days":
				invoices = invoices.filter((i: InvoiceManagementDto) =>
					this.is10DaysPast(i.dueDate)
				);
				break;
		}

		if (!this.showGemahnt) {
			invoices = invoices.filter((i: InvoiceManagementDto) => !i.hasMahnung);
		}

		invoices = invoices.filter((i: InvoiceManagementDto) =>
			this.filterRegions.includes(i.region)
		);

		invoices = invoices.filter(
			(i: InvoiceManagementDto) =>
				!this.$store.state.matchedInvoices.some(
					(mi: MatchedInvoiceDto) => mi.invoiceId === i.invoiceId
				)
		);

		if (this.validSearchTerm.length > 2) {
			invoices = invoices.filter(
				(i: InvoiceManagementDto) =>
					i.invoiceAddress.companyName
						.toUpperCase()
						.includes(this.validSearchTerm.toUpperCase()) ||
					i.invoiceAddress.givenName
						.toUpperCase()
						.includes(this.validSearchTerm.toUpperCase()) ||
					i.invoiceAddress.surname
						.toUpperCase()
						.includes(this.validSearchTerm.toUpperCase()) ||
					(i.invoiceAddress.eMail &&
						i.invoiceAddress.eMail
							.toUpperCase()
							.includes(this.validSearchTerm.toUpperCase())) ||
					i.customerAddress.companyName
						.toUpperCase()
						.includes(this.validSearchTerm.toUpperCase()) ||
					(i.customerAddress.givenName &&
						i.customerAddress.givenName
							.toUpperCase()
							.includes(this.validSearchTerm.toUpperCase())) ||
					(i.customerAddress.surname &&
						i.customerAddress.surname
							.toUpperCase()
							.includes(this.validSearchTerm.toUpperCase())) ||
					i.customerAddress.eMail
						.toUpperCase()
						.includes(this.validSearchTerm.toUpperCase()) ||
					i.customerId.toString().includes(this.validSearchTerm) ||
					i.orderId.toString().includes(this.validSearchTerm) ||
					i.invoiceId.toString().includes(this.validSearchTerm)
			);
		}

		return invoices;
	}
	private set invoices(newInvoices: InvoiceManagementDto[]) {
		this.$store.commit("setDueInvoices", newInvoices);
	}

	private get searchTerm(): string {
		return this.$store.state.dueInvoiceSearchTerm;
	}
	private set searchTerm(searchTerm: string) {
		this.$store.commit("setDueInvoiceSearchTerm", searchTerm);
	}

	private get totalDueInvoices() {
		return this.$store.state.dueInvoices.length;
	}
	private get totalAmountDueInvoicesChf() {
		return this.$store.state.dueInvoices
			.filter((i: InvoiceManagementDto) => i.currency === "CHF")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}
	private get totalAmountDueInvoicesEur() {
		return this.$store.state.dueInvoices
			.filter((i: InvoiceManagementDto) => i.currency === "EUR")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}

	private get currentFilterDueInvoices() {
		return this.invoices.length;
	}
	private get currentFilterAmountDueInvoicesChf() {
		return this.invoices
			.filter((i: InvoiceManagementDto) => i.currency === "CHF")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}
	private get currentFilterAmountDueInvoicesEur() {
		return this.invoices
			.filter((i: InvoiceManagementDto) => i.currency === "EUR")
			.reduce((acc: number, i: InvoiceManagementDto) => {
				return acc + (i.totalAmount - i.paidAmount);
			}, 0);
	}

	private get regions() {
		return this.$store.state.regions;
	}
	private get filterRegions() {
		return this.$store.state.invoiceFilterRegions;
	}
	private set filterRegions(regions: string[]) {
		this.$store.commit("setInvoiceFilterRegions", regions);
	}

	private get invoiceSorting() {
		return this.$store.state.dueInvoiceSorting;
	}
	private set invoiceSorting(invoiceSorting: any) {
		this.$store.commit("setDueInvoiceSorting", invoiceSorting);
	}

	private changeSort(column: string) {
		if (this.invoiceSorting.sortBy === column) {
			this.invoiceSorting.descending = !this.invoiceSorting.descending;
		} else {
			this.invoiceSorting.sortBy = column;
			this.invoiceSorting.descending = false;
		}
	}

	private async loadInvoices() {
		if (this.isLoading) {
			return;
		}
		try {
			this.isLoading = true;
			console.log("Loading open invoices...");
			const response = await httpClient().post("Invoice/GetAllOpenInvoices", null);
			let rawInvoices = response.data;
			rawInvoices.forEach((i: any) => {
				i.mahnungCost = 0.0;
			});
			this.invoices = rawInvoices;
		} catch (e) {
			console.log("Loading invoices failed.", e);
		} finally {
			this.isLoading = false;
		}
	}

	private async checkCreateMahnung(invoiceId: number, dueDate: Date) {
		this.currentInvoiceId = invoiceId;
		this.currentInvoiceDueDate = dueDate;
		this.mahnungNotDueInvoiceDialog = true;
	}

	private async savedMahnung() {
		this.$emit("reloadMahnungen");
		this.mahnungNotDueInvoiceDialog = false;
		await this.loadInvoices();
	}

	private async markAsPaid() {
		if (this.isLoading) {
			return;
		}
		try {
			this.isLoading = true;
			console.log(`Marking invoice ${this.markAsPaidInvoiceId} as paid...`);
			await httpClient().post(
				`Invoice/MarkInvoiceAsPaid?invoiceId=${this.markAsPaidInvoiceId}&amount=${this.markAsPaidInvoiceAmount}`,
				null
			);
			const paidInvoiceId = this.markAsPaidInvoiceId;
			this.$store.commit("removeDueInvoice", this.markAsPaidInvoiceId);
			this.markAsPaidDialogOpen = false;
			this.markAsPaidInvoiceAmount = 0.0;
			this.markAsPaidInvoiceId = 0;
			this.$emit("reloadMahnungen");

			this.paidInvoiceIds = [paidInvoiceId];
		} catch (e) {
			console.log("Marking invoice as paid failed.", e);
		} finally {
			this.isLoading = false;
		}
	}

	private goToCustomer(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private filterBySearchTerm() {
		if (this.searchTerm.length >= 3 || this.searchTerm.length === 0) {
			this.validSearchTerm = this.searchTerm;
		}
	}

	private navigateToPayments(invoideId: number) {
		this.$emit("navigateToPayments", invoideId);
	}

	private async navigateToInvoiceEdit(invoiceId: number) {
		this.$router.push({ name: "editInvoice", params: { invoiceId: invoiceId.toString() } });
	}

	private isDue(date: Date) {
		let dateNow = new Date();
		let compareDate = new Date(date);
		dateNow.setHours(0, 0, 0, 0);
		compareDate.setHours(0, 0, 0, 0);
		return compareDate <= dateNow;
	}

	private is10DaysPast(date: Date) {
		let dateNow = new Date();
		let compareDate = new Date(date);
		dateNow.setHours(0, 0, 0, 0);
		compareDate.setHours(0, 0, 0, 0);
		let diff = dateNow.getTime() - compareDate.getTime();
		if (diff <= 0) {
			return false;
		}
		diff = Math.abs(diff);
		return diff / (1000 * 60 * 60 * 24) >= 10;
	}
}
